import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { pricingForModel } from "components/Admin/Organizations/OrganizationDetail/KboFlowDebug/kboModelPricing";
import { makeObservable } from "mobx";
import { ClassificationService } from "services/ClassificationService";
import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import {
    getReclassifyHistoryControls,
    reclassifyHistoryColumns,
    reclassifyHistoryColumnsLlm,
    reclassifyHistoryColumnsV2,
} from "./ReclassifyHistoryColumns";

@AcxStore
export default class ReclassifyHistoryStore extends BaseStore {
    public static readonly Tasks = {
        LOAD_RECLASSIFY_RESULTS: "LoadReclassifyResults",
        LOAD_RECLASSIFY_RESULTS_V2: "LoadReclassifyResultsV2",
        LOAD_RECLASSIFY_RESULTS_LLM: "LoadReclassifyResultsLlm",
    };

    private readonly messageStore: MessageStore;

    private readonly classificationService: ClassificationService;

    // V1 (legacy) reclassify jobs
    public readonly dataGrid: AcxDataGridStore;

    // V2 Lucene (RBC) reclassify jobs
    public readonly dataGridV2: AcxDataGridStore;

    // V2 LLM (Model Builder) reclassify jobs
    public readonly dataGridLlm: AcxDataGridStore;

    constructor(private rootStore: IRootStore) {
        super("ReclassifyHistory");
        makeObservable(this);

        this.messageStore = rootStore.getStore(MessageStore);

        this.classificationService = new ClassificationService();

        // Each grid gets its own column copies: setColumns writes cached widths onto the objects it is given.
        this.dataGrid = new AcxDataGridStore("ReclassifyHistoryDataGridStore");
        this.dataGrid.checkboxSelection = false;
        this.dataGrid.setColumns(
            reclassifyHistoryColumns.map((c) => ({ ...c })),
        );
        this.dataGrid.title = "Activity Log";
        this.dataGrid.controls = getReclassifyHistoryControls(
            this,
            ReclassifyHistoryStore.Tasks.LOAD_RECLASSIFY_RESULTS,
            () => this.loadReclassifyResults(),
        );

        this.dataGridV2 = new AcxDataGridStore(
            "ReclassifyHistoryV2DataGridStore",
        );
        this.dataGridV2.checkboxSelection = false;
        this.dataGridV2.setColumns(
            reclassifyHistoryColumnsV2.map((c) => ({ ...c })),
        );
        this.dataGridV2.title = "Activity Log";
        this.dataGridV2.controls = getReclassifyHistoryControls(
            this,
            ReclassifyHistoryStore.Tasks.LOAD_RECLASSIFY_RESULTS_V2,
            () => this.loadReclassifyResultsV2(),
        );

        this.dataGridLlm = new AcxDataGridStore(
            "ReclassifyHistoryLlmDataGridStore",
        );
        this.dataGridLlm.checkboxSelection = false;
        this.dataGridLlm.setColumns(
            reclassifyHistoryColumnsLlm.map((c) => ({ ...c })),
        );
        this.dataGridLlm.title = "Activity Log";
        this.dataGridLlm.controls = getReclassifyHistoryControls(
            this,
            ReclassifyHistoryStore.Tasks.LOAD_RECLASSIFY_RESULTS_LLM,
            () => this.loadReclassifyResultsLlm(),
        );
    }

    public async loadReclassifyResults() {
        this.setupAsyncTask(
            ReclassifyHistoryStore.Tasks.LOAD_RECLASSIFY_RESULTS,
            async () => {
                this.dataGrid.setLoading(true);
                this.dataGrid.rows = [];
                try {
                    this.dataGrid.rows =
                        await this.classificationService.reclassifyResults();
                } catch (error: unknown) {
                    this.messageStore.logError(
                        "Failed to retrieve reclassify history. Please try again.",
                    );
                } finally {
                    this.dataGrid.setLoading(false);
                }
            },
        );
    }

    public async loadReclassifyResultsV2() {
        this.setupAsyncTask(
            ReclassifyHistoryStore.Tasks.LOAD_RECLASSIFY_RESULTS_V2,
            async () => {
                this.dataGridV2.setLoading(true);
                this.dataGridV2.rows = [];
                try {
                    this.dataGridV2.rows =
                        await this.classificationService.reclassifyResultsV2(
                            "Lucene",
                        );
                } catch (error: unknown) {
                    this.messageStore.logError(
                        "Failed to retrieve reclassify history. Please try again.",
                    );
                } finally {
                    this.dataGridV2.setLoading(false);
                }
            },
        );
    }

    public async loadReclassifyResultsLlm() {
        this.setupAsyncTask(
            ReclassifyHistoryStore.Tasks.LOAD_RECLASSIFY_RESULTS_LLM,
            async () => {
                this.dataGridLlm.setLoading(true);
                this.dataGridLlm.rows = [];
                try {
                    const results =
                        await this.classificationService.reclassifyResultsV2(
                            "LLM",
                        );
                    // Totals and cost per job, priced the same way as the reclassify dialog's estimate
                    this.dataGridLlm.rows = results.map((result) => {
                        let inputTokens = 0;
                        let cachedInputTokens = 0;
                        let outputTokens = 0;
                        let estimatedCostUsd = 0;
                        const unpricedModels: string[] = [];
                        for (const usage of result.tokenUsage) {
                            inputTokens += usage.inputTokens;
                            cachedInputTokens += usage.cachedInputTokens;
                            outputTokens += usage.outputTokens;

                            const pricing = pricingForModel(usage.modelName);
                            if (!pricing) {
                                unpricedModels.push(usage.modelName);
                                continue;
                            }
                            // Cached input tokens are a subset of input tokens, billed at the cached rate
                            const cached = Math.min(
                                usage.cachedInputTokens,
                                usage.inputTokens,
                            );
                            estimatedCostUsd +=
                                ((usage.inputTokens - cached) / 1_000_000) *
                                    pricing.input +
                                (cached / 1_000_000) * pricing.cachedInput +
                                (usage.outputTokens / 1_000_000) *
                                    pricing.output;
                        }

                        const hasUsage = result.tokenUsage.length > 0;
                        return {
                            ...result,
                            inputTokens: hasUsage ? inputTokens : undefined,
                            cachedInputTokens: hasUsage
                                ? cachedInputTokens
                                : undefined,
                            outputTokens: hasUsage ? outputTokens : undefined,
                            // left empty when no model on the job has a known price
                            estimatedCostUsd:
                                unpricedModels.length < result.tokenUsage.length
                                    ? estimatedCostUsd
                                    : undefined,
                            unpricedModels,
                        };
                    });
                } catch (error: unknown) {
                    this.messageStore.logError(
                        "Failed to retrieve reclassify history. Please try again.",
                    );
                } finally {
                    this.dataGridLlm.setLoading(false);
                }
            },
        );
    }
}
